import { useCallback, useEffect, useMemo, useRef } from 'react';
import Vec2 from 'vec2';

import { useChipMinimizerContext } from '../../chipMinimizerContext';
import { useDrawingShapeContext } from '../../drawingShapeContext';
import { useCanvasUpdateHandler } from '../../fabricCanvasContext/hooks';
import { useFaucetContext } from '../../faucetsContext';
import { useOptionalProductContext } from '../../optionalProductContext';
import { usePricedProductsContext } from '../../pricedProductsContext';
import { useProductDetailsContext } from '../../productDetailsContext';
import { useUndoStackContext } from '../../undoStackContext';
import {
  createDShapeFromCloneEstimateShape,
  refactorModelToApplianceModel,
} from './useRestoreEstimateData.utils';

import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { FAUX_SHAPE_INDEX } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import useAppliance from '~/core/hooks/useAppliance';
import {
  DrawingShape,
  Estimate,
  SelectedItemDetailEntry,
  SelectedItemProperties,
} from '~/core/models';
import { GetEstimateDto } from '~/core/models/save-estimate-dto';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectRestorableColorId,
  selectSelectedColorId,
  updateRestorableColorId,
  updateSelectedColorId,
} from '~/redux/slices/color';
import { setCustomCutouts } from '~/redux/slices/cutout';
import {
  getCutoutsFromEstimate,
  getCutoutsFromShapes,
  mergeAllCutouts,
} from '~/redux/slices/cutout/cutout.utils';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { updateApplySameEdge } from '~/redux/slices/edge';
import { selectIsNewEstimate } from '~/redux/slices/estimate';
import {
  selectSelectedMaterial,
  updateRestorableMaterial,
  updateSelectedMaterial,
} from '~/redux/slices/material';
import {
  selectSelectedDrawingObjectName,
  updateSelectedDrawingObjectName,
  updateSelectedDrawingTab,
} from '~/redux/slices/shape';
import { enableAllSteps } from '~/redux/slices/stepper';
import { updateRestorableStoreId } from '~/redux/slices/store';
import { updateSelectedTearOuts } from '~/redux/slices/tearOut';
import { visitAllSteps } from '~/redux/slices/visitedSteps';
import { updateZipcode } from '~/redux/slices/zipcode';

export const useRestoreEstimateData = () => {
  const selectedMaterial = useSelectedMaterial();

  const restorableColorId = useAppSelector(selectRestorableColorId);
  const selectedColorId = useAppSelector(selectSelectedColorId);
  const currentMaterial = useAppSelector(selectSelectedMaterial);
  const selectedDrawingObjectName = useAppSelector(
    selectSelectedDrawingObjectName
  );
  const isNewEstimate = useAppSelector(selectIsNewEstimate);

  const dispatch = useAppDispatch();

  const {
    getters: { activeDrawingShape, activeDrawingItem },
    handlers: {
      updateDrawingShapes,
      updateActiveDrawingShapeId,
      resetDrawingShapes,
    },
  } = useDrawingShapeContext();
  const { clearUndoStack } = useUndoStackContext();
  const { resetAllPricing } = usePricedProductsContext();
  const { updateRestorableFaucets } = useFaucetContext();
  const { setRestorableChipMinimizers } = useChipMinimizerContext();
  const { setRestorableOptionalProducts } = useOptionalProductContext();
  const { setRestorableCatalog } = useProductDetailsContext();

  const { updateShapesDimensions } = useCanvasUpdateHandler();
  const { syncCookingAppliancesCount } = useAppliance();

  const hasRestoredEstimateData = useRef<boolean>(isNewEstimate);

  const buildShapeListFromSavedEstimate = useCallback(
    (estimate: Estimate): Array<DrawingShape> => {
      const dShapes: Array<DrawingShape> = [];

      [...(estimate.drawingShapes ?? [])]?.forEach((shape, idx) => {
        if (idx === FAUX_SHAPE_INDEX) {
          // restore faux shape to get appliances out in the middle of the kitchen again
          const fShape = drawingTool.buildFauxShape();

          if (shape.appliances?.length) {
            const refactoredAppliances = refactorModelToApplianceModel(
              shape.appliances
            );

            const restoredAppliances = refactoredAppliances.map(
              (appliance) => ({
                ...appliance,
                centerPoint: new Vec2(
                  appliance.centerPoint.x,
                  appliance.centerPoint.y
                ),
              })
            );

            fShape.appliances = [...restoredAppliances];
          }
          dShapes.push(fShape);
        } else {
          if (!shape.skeleton?.length) {
            return;
          }

          dShapes.push(createDShapeFromCloneEstimateShape(shape));
        }
      });

      return dShapes;
    },
    []
  );

  const restoreFromFetchedEstimate = useCallback(
    (estimate: GetEstimateDto): void => {
      const estimateData = estimate.estimateData;

      // This could go away and just read from estimate directly
      dispatch(updateRestorableStoreId(estimate.storeId));
      dispatch(updateZipcode(estimateData?.zipCode ?? estimate.customerZip));

      //TODO: move this to a redux slice
      clearUndoStack();
      resetDrawingShapes();
      resetAllPricing();

      dispatch(
        updateRestorableMaterial({
          description: estimateData.materialDesc,
          qteGrpID: estimateData.qteGrpID,
        })
      );

      if (estimateData.tearOuts) {
        const tearOuts = Object.entries(estimateData.tearOuts).reduce<
          Record<number, number>
        >((acc, [id, value]) => ({ ...acc, [id]: value.quantity }), {});

        dispatch(updateSelectedTearOuts(tearOuts));
      }

      if (estimateData.cutOuts) {
        const cutoutsOnShapes = getCutoutsFromShapes(estimateData);
        const cutoutsOnCards = getCutoutsFromEstimate(estimateData);
        const allCutouts = mergeAllCutouts(cutoutsOnCards, cutoutsOnShapes);

        dispatch(setCustomCutouts(allCutouts));
      }

      if (estimateData.chipMinimizers) {
        setRestorableChipMinimizers(estimateData.chipMinimizers);
      }

      for (const [key, item] of Object.entries(estimateData.optionalProducts)) {
        const keyNum = parseInt(key);

        if (item.quantity > 0 && item.note && !item.detailEntries?.length) {
          const entry: SelectedItemDetailEntry = {
            quantity: item.quantity,
            note: item.note,
          };
          const updatedItem: SelectedItemProperties = {
            ...item,
            detailEntries: [entry],
          };

          estimateData.optionalProducts[keyNum] = updatedItem;
        }
      }

      if (Object.keys(estimateData.optionalProducts).length > 0) {
        setRestorableOptionalProducts(estimateData.optionalProducts);
      }

      if (estimateData.faucets) {
        updateRestorableFaucets(estimateData.faucets);
      }

      if (estimateData.color) {
        dispatch(updateRestorableColorId(estimateData.color.productId));
      }

      if (estimateData.productCatalog) {
        setRestorableCatalog(estimateData.productCatalog);
      }

      dispatch(updateApplySameEdge(estimateData.applySameEdge));

      const _drawingShapes = buildShapeListFromSavedEstimate(estimateData);

      const drawingShapes = [...drawingTool.cloneShapes(_drawingShapes)].map(
        (o) => Object.assign(Object.create(Object.getPrototypeOf(o)), o)
      );

      updateShapesDimensions({
        color: estimateData.color,
        drawingShapes,
      });

      updateDrawingShapes([...drawingShapes]);
      if (activeDrawingShape?.id !== drawingShapes[FAUX_SHAPE_INDEX + 1].id) {
        updateActiveDrawingShapeId(drawingShapes[FAUX_SHAPE_INDEX + 1].id);
      }

      dispatch(updateNeedRedrawCanvas(true));
      dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
    },
    [
      dispatch,
      activeDrawingShape?.id,
      buildShapeListFromSavedEstimate,
      clearUndoStack,
      resetAllPricing,
      resetDrawingShapes,
      setRestorableCatalog,
      setRestorableChipMinimizers,
      setRestorableOptionalProducts,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
      updateRestorableFaucets,
      updateShapesDimensions,
    ]
  );

  const isEstimateDataFullyRestored = useMemo(
    () =>
      restorableColorId === selectedColorId &&
      selectedMaterial?.id === currentMaterial?.id &&
      activeDrawingItem?.type === 'shape' &&
      (activeDrawingItem?.item as DrawingShape)?.label ===
        selectedDrawingObjectName,
    [
      activeDrawingItem?.item,
      activeDrawingItem?.type,
      currentMaterial?.id,
      restorableColorId,
      selectedColorId,
      selectedDrawingObjectName,
      selectedMaterial?.id,
    ]
  );

  const restoreLoadedEstimateData = useCallback(() => {
    if (restorableColorId && restorableColorId !== selectedColorId) {
      dispatch(updateSelectedColorId(restorableColorId));
    }

    if (selectedMaterial && selectedMaterial.id !== currentMaterial?.id) {
      dispatch(updateSelectedMaterial(selectedMaterial));
    }

    if (activeDrawingItem?.type === 'shape') {
      const activeDrawingShapeLabel = (activeDrawingItem?.item as DrawingShape)
        ?.label;

      if (
        activeDrawingShapeLabel &&
        activeDrawingShapeLabel !== selectedDrawingObjectName
      ) {
        dispatch(updateSelectedDrawingObjectName(activeDrawingShapeLabel));
      }
    }

    // Force canvas redraw after estimate restoration to ensure proper rendering
    setTimeout(() => {
      dispatch(updateNeedRedrawCanvas(true));
    }, 200);
  }, [
    activeDrawingItem?.item,
    activeDrawingItem?.type,
    currentMaterial?.id,
    dispatch,
    restorableColorId,
    selectedColorId,
    selectedDrawingObjectName,
    selectedMaterial,
  ]);

  useEffect(() => {
    if (!isNewEstimate && !hasRestoredEstimateData.current) {
      restoreLoadedEstimateData();
      if (isEstimateDataFullyRestored) {
        hasRestoredEstimateData.current = true;
        dispatch(enableAllSteps());
        dispatch(visitAllSteps());
      }
    }
  }, [
    dispatch,
    isEstimateDataFullyRestored,
    isNewEstimate,
    restoreLoadedEstimateData,
  ]);

  useEffect(() => {
    syncCookingAppliancesCount();
  }, [syncCookingAppliancesCount]);

  return { restoreFromFetchedEstimate };
};
